/**
 * useMarcheKpiCards — Hook composé pour la Section KPI Cards Samsung.
 *
 * Agrège en parallèle les 6 KPIs Samsung-centred conformes wireframe §4.2 :
 *   KC.1 PDM Volume Samsung  — usePdmVolume
 *   KC.2 PDM Valeur Samsung  — usePdmValue
 *   KC.3 PDM Stock Samsung   — usePdmStock
 *   KC.4 ND Samsung          — useNd
 *   KC.5 WD Samsung          — useWd
 *   KC.6 IHS Volume Samsung  — useIhs (global breakdown)
 *
 * Correctif V1.bis — fix déviation wireframe marche_retail.md §4.2.
 * Remplace la version précédente (execSummary + pdmVolume + pdmStock) qui était
 * mappée sur l'Executive Summary (KPI 12) au lieu des 6 KPI Samsung-centred.
 *
 * spec_kpis.md §3-§7-§11 · wireframes/marche_retail.md §4.2 · DECISIONS #093
 */

import { usePdmVolume } from './kpis/usePdmVolume';
import { usePdmValue } from './kpis/usePdmValue';
import { usePdmStock } from './kpis/usePdmStock';
import { useNd } from './kpis/useNd';
import { useWd } from './kpis/useWd';
import { useIhs } from './kpis/useIhs';
import type {
  KpiApiResponse,
  PdmVolumeData,
  PdmValueData,
  PdmStockData,
  NdData,
  WdData,
  IhsData,
  MarcheQueryParams,
} from '../types/marche.types';

export interface MarcheKpiCardsResult {
  pdmVolume: {
    data: KpiApiResponse<PdmVolumeData> | undefined;
    isLoading: boolean;
    isError: boolean;
    error: Error | null;
  };
  pdmValue: {
    data: KpiApiResponse<PdmValueData> | undefined;
    isLoading: boolean;
    isError: boolean;
    error: Error | null;
  };
  pdmStock: {
    data: KpiApiResponse<PdmStockData> | undefined;
    isLoading: boolean;
    isError: boolean;
    error: Error | null;
  };
  nd: {
    data: KpiApiResponse<NdData> | undefined;
    isLoading: boolean;
    isError: boolean;
    error: Error | null;
  };
  wd: {
    data: KpiApiResponse<WdData> | undefined;
    isLoading: boolean;
    isError: boolean;
    error: Error | null;
  };
  ihs: {
    data: KpiApiResponse<IhsData> | undefined;
    isLoading: boolean;
    isError: boolean;
    error: Error | null;
  };
  isAnyLoading: boolean;
}

/**
 * Hook composé — 6 requêtes parallèles pour les KPI Cards Samsung conformes wireframe §4.2.
 *
 * @param params — filtres API communs (period, channel, geo, etc.)
 */
export function useMarcheKpiCards(
  params?: MarcheQueryParams,
  options?: { includeRef4w?: boolean; includeRefW1?: boolean; includeRefSwly?: boolean },
): MarcheKpiCardsResult {
  // D-KPI-CARDS-DELTA-W1 — `refs` porte les TROIS références (4W, W-1, SWLY).
  // Les 4 KPIs snapshot n'ont pas de série hebdo : sans refW1, le toggle W-1
  // laissait 4 cartes sur 6 sans delta.
  //
  // ⚠ LES TROIS SONT DEMANDÉES ENSEMBLE, ET C'EST VOULU. Les références voyagent
  //   dans la MÊME réponse que la valeur courante : n'en demander qu'une par mode
  //   ferait changer la queryKey à chaque bascule du sélecteur, donc refetcher le
  //   payload principal — les 6 cartes repasseraient en squelette à chaque clic.
  //   On paie une requête de plus au chargement pour que le sélecteur reste
  //   instantané. SWLY n'ajoute d'ailleurs aucune requête KPI tant que N-1 est
  //   absent de la base : la résolution s'arrête avant (cf. include_ref_swly).
  const refs = {
    includeRef4w:   options?.includeRef4w   ?? false,
    includeRefW1:   options?.includeRefW1   ?? false,
    includeRefSwly: options?.includeRefSwly ?? false,
  };
  // ⚠ PDM Volume/Valeur reçoivent les MÊMES références que les quatre autres
  //   depuis l'unification du rang 1 (2026-08-04). Ils dérivaient leur delta
  //   d'une série hebdomadaire ; ce second mécanisme est supprimé.
  const pdmVolume = usePdmVolume(params, refs);
  const pdmValue  = usePdmValue(params, refs);
  // V4.10 — pdmStock/nd/wd/ihs supportent include_ref4w pour collapse 4 fetches.
  const pdmStock  = usePdmStock(params, refs);
  const nd        = useNd(params, refs);
  const wd        = useWd(params, refs);
  // IHS en mode global par défaut — breakdown optionnel depuis les filtres
  const ihs       = useIhs({ ...params, breakdown: 'global' }, refs);

  return {
    pdmVolume: {
      data: pdmVolume.data,
      isLoading: pdmVolume.isLoading,
      isError: pdmVolume.isError,
      error: pdmVolume.error,
    },
    pdmValue: {
      data: pdmValue.data,
      isLoading: pdmValue.isLoading,
      isError: pdmValue.isError,
      error: pdmValue.error,
    },
    pdmStock: {
      data: pdmStock.data,
      isLoading: pdmStock.isLoading,
      isError: pdmStock.isError,
      error: pdmStock.error,
    },
    nd: {
      data: nd.data,
      isLoading: nd.isLoading,
      isError: nd.isError,
      error: nd.error,
    },
    wd: {
      data: wd.data,
      isLoading: wd.isLoading,
      isError: wd.isError,
      error: wd.error,
    },
    ihs: {
      data: ihs.data,
      isLoading: ihs.isLoading,
      isError: ihs.isError,
      error: ihs.error,
    },
    isAnyLoading:
      pdmVolume.isLoading ||
      pdmValue.isLoading  ||
      pdmStock.isLoading  ||
      nd.isLoading        ||
      wd.isLoading        ||
      ihs.isLoading,
  };
}
